import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ExternalLink, ShieldAlert, Clock, CheckCircle2, Radio } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { usePanelT } from "../../hooks/usePanelT";

const NET = {
    USDT_TRC20: { name: "TRON (TRC-20)", short: "TRC-20", color: "#EF0027", eta: "≈ 1–3", explorer: (a) => `https://tronscan.org/#/address/${a}` },
    USDT_ERC20: { name: "Ethereum (ERC-20)", short: "ERC-20", color: "#627EEA", eta: "≈ 3–10", explorer: (a) => `https://etherscan.io/address/${a}` },
    USDC_ERC20: { name: "Ethereum (ERC-20)", short: "ERC-20", color: "#627EEA", eta: "≈ 3–10", explorer: (a) => `https://etherscan.io/address/${a}` },
};

function fmtUsd(n) {
    return `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function LiveStatus({ currency, openedAt }) {
    const { t } = usePanelT();
    const [dep, setDep] = useState(null);
    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const { data } = await api.get("/me/deposits");
                const mine = (data || []).filter((d) => d.currency === currency);
                const fresh = mine.find((d) => d.status !== "credited") || mine.find((d) => new Date(d.credited_at || d.first_seen_at).getTime() >= openedAt - 60000);
                if (alive) setDep(fresh || null);
            } catch { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 12000);
        return () => { alive = false; clearInterval(id); };
    }, [currency, openedAt]);

    if (dep?.status === "credited") {
        return (
            <div data-testid="deposit-live-status" data-state="credited" className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] px-4 py-3 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <div className="text-sm"><p className="font-semibold text-emerald-300">{t("dp.creditedTitle")}</p><p className="text-xs text-emerald-200/80">+{fmtUsd(dep.amount_usd)} · {dep.amount_crypto?.toFixed(2)} {currency.split("_")[0]}</p></div>
            </div>
        );
    }
    if (dep) {
        return (
            <div data-testid="deposit-live-status" data-state="confirming" className="rounded-xl border border-yellow-500/30 bg-yellow-500/[0.08] px-4 py-3 flex items-center gap-3">
                <Clock className="w-5 h-5 text-yellow-300 shrink-0 animate-pulse" />
                <div className="text-sm"><p className="font-semibold text-yellow-200">{t("dp.detected")}</p><p className="text-xs text-yellow-100/80">{dep.confirmations}/{dep.confirmations_required} · +{fmtUsd(dep.amount_usd)}</p></div>
            </div>
        );
    }
    return (
        <div data-testid="deposit-live-status" data-state="waiting" className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 flex items-center gap-3">
            <Radio className="w-4 h-4 text-primary shrink-0 animate-pulse" />
            <p className="text-xs text-muted-foreground">{t("dp.waiting")}</p>
        </div>
    );
}

export default function DepositDialog({ currency, open, onOpenChange }) {
    const { t } = usePanelT();
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);
    const openedAt = useRef(Date.now());
    const net = NET[currency.code];
    const token = currency.code.split("_")[0];

    useEffect(() => {
        if (!open) return;
        openedAt.current = Date.now();
        setData(null);
        api.post("/me/deposit/initiate", { currency: currency.code })
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("dp.loadFail")));
    }, [open, currency.code, t]);

    const copy = async () => {
        if (!data?.address) return;
        try {
            await navigator.clipboard.writeText(data.address);
            setCopied(true);
            toast.success(t("dp.copied"));
            setTimeout(() => setCopied(false), 1800);
        } catch {
            const range = document.createRange();
            const el = document.querySelector('[data-testid="deposit-address"]');
            if (el) { range.selectNodeContents(el); const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range); }
            toast.message(t("dp.copyBlocked"));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="deposit-dialog" className="bg-[#0d0d10] border-white/10 max-w-2xl p-0 overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-3">
                    <DialogTitle className="font-display text-xl flex items-center gap-3">
                        <span className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center" style={{ color: currency.color }}><currency.Icon className="w-5 h-5" /></span>
                        <span>{token} {t("dp.deposit")} <span className="text-muted-foreground font-normal text-base">· {net.name}</span></span>
                    </DialogTitle>
                    <DialogDescription>{t("dp.sub")}</DialogDescription>
                </DialogHeader>

                <div className="px-6 pb-6 grid md:grid-cols-[220px_1fr] gap-6">
                    <div className="flex flex-col items-center gap-3">
                        <div data-testid="deposit-qr" className="rounded-2xl bg-white p-3 shadow-[0_0_40px_rgba(255,255,255,0.08)]">
                            {data?.address ? <QRCodeSVG value={data.address} size={196} level="M" bgColor="#ffffff" fgColor="#0A0A0B" /> : <div className="w-[196px] h-[196px] grid place-items-center text-xs text-black/50">{t("loading")}</div>}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
                            <span className="px-2 py-0.5 rounded-full border" style={{ color: currency.color, borderColor: `${currency.color}55` }}>{token}</span>
                            <span className="px-2 py-0.5 rounded-full border" style={{ color: net.color, borderColor: `${net.color}66` }} data-testid="deposit-network-badge">{net.short}</span>
                        </div>
                    </div>

                    <div className="space-y-4 min-w-0">
                        <div className="space-y-2">
                            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">{t("dp.yourAddress")}</p>
                            <div data-testid="deposit-address" className="font-mono text-[13px] bg-background border border-white/10 rounded-xl p-3 break-all select-all leading-relaxed">{data?.address || "…"}</div>
                            <div className="flex gap-2">
                                <Button onClick={copy} disabled={!data?.address} data-testid="deposit-copy" className="flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} {copied ? t("copied") : t("dp.copy")}
                                </Button>
                                {data?.address && (
                                    <a href={net.explorer(data.address)} target="_blank" rel="noopener noreferrer" data-testid="deposit-explorer" className="inline-flex items-center justify-center h-11 px-3 rounded-md border border-white/10 hover:bg-white/[0.05] text-xs"><ExternalLink className="w-4 h-4" /></a>
                                )}
                            </div>
                        </div>

                        <div data-testid="deposit-network-warning" className="rounded-xl border border-red-500/30 bg-red-500/[0.07] px-3 py-2.5 flex gap-2.5 text-xs leading-relaxed">
                            <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                            <p className="text-red-100/90 font-medium">{t("dp.warn", { token, net: net.name })}</p>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-center">
                            {[
                                [t("dp.confirmations"), data ? `${data.confirmations_required}` : "—"],
                                [t("dp.eta"), `${net.eta} ${t("dp.min")}`],
                                [t("dp.rate"), `1 ${token} = $1`],
                            ].map(([l, v]) => (
                                <div key={l} className="rounded-lg bg-white/[0.03] border border-white/[0.06] py-2">
                                    <p className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{l}</p>
                                    <p className="text-sm font-semibold tabular-nums mt-0.5">{v}</p>
                                </div>
                            ))}
                        </div>

                        {open && <LiveStatus currency={currency.code} openedAt={openedAt.current} />}

                        <p className="text-[11px] text-muted-foreground">{t("dp.permanent")}</p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
